import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { 
  ShoppingCartIcon, 
  TrashIcon,
  ArrowRightIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const CartPage = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [summary, setSummary] = useState({
    total: 0,
    savings: 0,
    itemCount: 0,
    allItemsHaveUrls: false
  });
  const [urlInputs, setUrlInputs] = useState({});
  const [urlDebounceTimers, setUrlDebounceTimers] = useState({});
  const [urlErrors, setUrlErrors] = useState({});
  const [isNavigatingToCheckout, setIsNavigatingToCheckout] = useState(false);

  useEffect(() => {
    // Wait for auth to resolve. Visitors arriving from a cart email link who
    // aren't signed in are sent to login and returned to /cart afterward.
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?redirect=/cart', { replace: true });
      return;
    }
    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  // Initialize URL inputs when cart loads
  useEffect(() => {
    if (cart.items.length > 0) {
      const initialUrls = {};
      cart.items.forEach(item => {
        initialUrls[item._id] = item.url || '';
      });
      setUrlInputs(initialUrls);
    }
  }, [cart.items]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(urlDebounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [urlDebounceTimers]);

  const fetchCart = async () => {
    try {
      const response = await api.get('/api/cart');
      setCart(response.data.cart);
      fetchCartSummary();
    } catch (error) {
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const fetchCartSummary = async () => {
    try {
      const response = await api.get('/api/cart/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch cart summary:', error);
    }
  };

  const updateCartItem = async (itemId, updates) => {
    setUpdating(prev => ({ ...prev, [itemId]: true }));
    try {
      const response = await api.put(`/api/cart/update/${itemId}`, updates);
      setCart(response.data.cart);
      fetchCartSummary();
      toast.success('Cart updated successfully');
      // Notify navbar of cart update
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      toast.error('Failed to update cart');
    } finally {
      setUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      const response = await api.delete(`/api/cart/remove/${itemId}`);
      setCart(response.data.cart);
      fetchCartSummary();
      toast.success('Item removed from cart');
      // Notify navbar of cart update
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    try {
      const response = await api.delete('/api/cart/clear');
      setCart(response.data.cart);
      fetchCartSummary();
      toast.success('Cart cleared');
      // Notify navbar of cart update
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  const handleBillingCycleChange = (itemId, newCycle) => {
    updateCartItem(itemId, { billingCycle: newCycle });
  };

  // URL validation function
  const isValidUrl = (url) => {
    if (!url || url.trim() === '') return false;
    
    try {
      // Check if it has a protocol
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return false;
      }
      
      // Try to create a URL object to validate format
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Debounced URL update function
  const debouncedUrlUpdate = useCallback((itemId, url) => {
    // Clear existing timer
    if (urlDebounceTimers[itemId]) {
      clearTimeout(urlDebounceTimers[itemId]);
    }

    // Set new timer
    const timer = setTimeout(() => {
      // Only update if URL is valid
      if (isValidUrl(url)) {
        updateCartItem(itemId, { url });
      }
    }, 1000); // 1 second delay

    setUrlDebounceTimers(prev => ({
      ...prev,
      [itemId]: timer
    }));
  }, [urlDebounceTimers]);

  const handleUrlChange = (itemId, url) => {
    // Update local state immediately for responsive UI
    setUrlInputs(prev => ({
      ...prev,
      [itemId]: url
    }));

    // Clear error when user starts typing
    if (urlErrors[itemId]) {
      setUrlErrors(prev => ({
        ...prev,
        [itemId]: null
      }));
    }

    // Debounce the API call
    debouncedUrlUpdate(itemId, url);
  };

  const handleUrlFocus = (itemId) => {
    const currentUrl = urlInputs[itemId] || '';
    if (!currentUrl.startsWith('http://') && !currentUrl.startsWith('https://')) {
      const newUrl = currentUrl ? `https://${currentUrl}` : 'https://';
      setUrlInputs(prev => ({
        ...prev,
        [itemId]: newUrl
      }));
    }
  };

  const handleUrlBlur = (itemId) => {
    const url = urlInputs[itemId] || '';
    
    if (url.trim() !== '' && !isValidUrl(url)) {
      setUrlErrors(prev => ({
        ...prev,
        [itemId]: 'Please enter a valid URL (e.g., https://example.com)'
      }));
    } else {
      setUrlErrors(prev => ({
        ...prev,
        [itemId]: null
      }));
    }
  };

  const handleProceedToCheckout = async () => {
    // Check if all items have valid URLs using local state
    const allItemsHaveValidUrls = cart.items.every(item => {
      const url = urlInputs[item._id] || '';
      return isValidUrl(url);
    });

    if (!allItemsHaveValidUrls) {
      toast.error('Please enter valid URLs for all hosting plans');
      return;
    }

    setIsNavigatingToCheckout(true);
    const savingToastId = toast.loading('Saving URLs...', { id: 'saving-urls' });
    
    try {
      // Clear all pending debounce timers
      Object.values(urlDebounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      setUrlDebounceTimers({});

      // Force update any URLs that differ from what's in the cart
      const pendingUpdates = [];
      cart.items.forEach(item => {
        const url = urlInputs[item._id] || '';
        if (isValidUrl(url) && url !== (item.url || '')) {
          pendingUpdates.push(updateCartItem(item._id, { url }));
        }
      });

      // Wait for all pending updates to complete
      if (pendingUpdates.length > 0) {
        await Promise.all(pendingUpdates);
      }
      
      // Refresh cart from server to get latest data with saved URLs
      await fetchCart();
      
      // Get the latest cart data after refresh
      const response = await api.get('/api/cart');
      const latestCart = response.data.cart;
      
      // Double-check all items have URLs in the actual cart
      const allItemsHaveUrlsInCart = latestCart.items.every(item => {
        return item.url && isValidUrl(item.url);
      });

      if (!allItemsHaveUrlsInCart) {
        toast.error('Please wait for URLs to be saved and try again', { id: 'saving-urls' });
        setIsNavigatingToCheckout(false);
        return;
      }

      // Get updated summary
      const summaryResponse = await api.get('/api/cart/summary');
      const latestSummary = summaryResponse.data;
      
      toast.dismiss('saving-urls');
      setIsNavigatingToCheckout(false);
      
      // Navigate to checkout with latest cart data
      navigate('/checkout', { 
        state: { 
          cart: latestCart,
          total: latestSummary.total,
          savings: latestSummary.savings
        } 
      });
    } catch (error) {
      console.error('Error saving URLs:', error);
      toast.error('Failed to save URLs. Please try again.', { id: 'saving-urls' });
      setIsNavigatingToCheckout(false);
    }
  };

  const getPlanFeatures = (planType) => {
    const features = {
      shared: [
        '5GB SSD Storage',
        'Unlimited Bandwidth',
        'Free SSL Certificate',
        'Daily Backups',
        'cPanel Control Panel',
        '99.9% Uptime Guarantee'
      ],
      private: [
        '20GB SSD Storage',
        'Unlimited Bandwidth',
        'Free SSL Certificate',
        'Daily Backups',
        'cPanel Control Panel',
        '99.9% Uptime Guarantee',
        'Malware Scanning',
        'SSH Access'
      ],
      premium: [
        '50GB SSD Storage',
        'Unlimited Bandwidth',
        'Free SSL Certificate',
        'Daily Backups',
        'cPanel Control Panel',
        '99.9% Uptime Guarantee',
        'Malware Scanning',
        'SSH Access',
        'Staging Environment',
        'CDN Setup',
        'Performance Monitoring',
        'Phone Support'
      ]
    };
    return features[planType] || [];
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'shared':
        return '🏠';
      case 'private':
        return '🏢';
      case 'premium':
        return '⭐';
      default:
        return '🌐';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cart - The Host Bros</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Your Cart</h1>
            <p className="text-gray-600 mt-2">
              Review your hosting plans and complete your purchase.
            </p>
          </div>

          {cart.items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCartIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
              <p className="text-gray-600 mb-6">
                Add some hosting plans to get started.
              </p>
              <button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    const element = document.querySelector('#hosting-plans');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="btn-primary"
              >
                Browse Plans
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="space-y-6">
                  {cart.items.map((item) => item.planType === 'domain' ? (
                    <div
                      key={item._id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">🌐</span>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Domain Name</h3>
                            <p className="text-sm text-gray-600">Yearly subscription</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">${item.price}</p>
                          <p className="text-sm text-gray-500">per year</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Domain
                        </label>
                        <div className="relative">
                          <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <div className="input-field pl-10 bg-gray-50 break-all">
                            {item.url || '—'}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={() => removeFromCart(item._id)}
                          className="text-red-600 hover:text-red-700 flex items-center text-sm"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={item._id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{getPlanIcon(item.planType)}</span>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 capitalize">
                              {item.planType} Hosting
                            </h3>
                            <p className="text-sm text-gray-600 capitalize">
                              {item.billingCycle} billing
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">${item.price}</p>
                          <p className="text-sm text-gray-500 capitalize">
                            per {item.billingCycle}
                          </p>
                        </div>
                      </div>

                      {/* URL Input */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Website URL <span className="text-red-500">*</span>
                        </label>
                        <div className="flex">
                          <div className="relative flex-1">
                            <GlobeAltIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
                              urlErrors[item._id] ? 'text-red-400' : 'text-gray-400'
                            }`} />
                            <input
                              type="url"
                              value={urlInputs[item._id] || ''}
                              onChange={(e) => handleUrlChange(item._id, e.target.value)}
                              className={`input-field pl-10 ${
                                urlErrors[item._id] 
                                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                  : ''
                              }`}
                              placeholder="https://yourwebsite.com"
                              disabled={updating[item._id]}
                              onFocus={(e) => handleUrlFocus(item._id)}
                              onBlur={(e) => handleUrlBlur(item._id)}
                            />
                          </div>
                        </div>
                        {urlErrors[item._id] && (
                          <p className="text-sm text-red-600 mt-1 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {urlErrors[item._id]}
                          </p>
                        )}
                      </div>

                      {/* Billing Cycle Toggle */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Billing Cycle
                        </label>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleBillingCycleChange(item._id, 'monthly')}
                            disabled={updating[item._id]}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              item.billingCycle === 'monthly'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Monthly
                          </button>
                          <button
                            onClick={() => handleBillingCycleChange(item._id, 'yearly')}
                            disabled={updating[item._id]}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              item.billingCycle === 'yearly'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Yearly (Save ${item.planType === 'shared' ? 30 : item.planType === 'private' ? 40 : 60})
                          </button>
                        </div>
                      </div>

                      {/* Plan Features */}
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">What's included:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {getPlanFeatures(item.planType).slice(0, 6).map((feature, index) => (
                            <div key={index} className="flex items-center text-sm text-gray-600">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              {feature}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={() => removeFromCart(item._id)}
                          className="text-red-600 hover:text-red-700 flex items-center text-sm"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Clear Cart Button */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={clearCart}
                    className="text-gray-600 hover:text-gray-700 text-sm"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Subtotal ({summary.itemCount} items)</span>
                      <span className="font-medium">${summary.total}</span>
                    </div>
                    
                    {summary.savings > 0 && (
                      <div className="flex justify-between items-center text-green-600">
                        <span>Yearly Savings</span>
                        <span>-${summary.savings}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-gray-900">${summary.total}</span>
                    </div>
                  </div>

                  {/* Proceed to Checkout Button */}
                  <button
                    onClick={handleProceedToCheckout}
                    disabled={(() => {
                      const allItemsHaveValidUrls = cart.items.every(item => {
                        const url = urlInputs[item._id] || '';
                        return isValidUrl(url);
                      });
                      return !allItemsHaveValidUrls || summary.itemCount === 0 || isNavigatingToCheckout;
                    })()}
                    className={`w-full py-3 px-4 rounded-md font-medium transition-colors flex items-center justify-center ${
                      (() => {
                        const allItemsHaveValidUrls = cart.items.every(item => {
                          const url = urlInputs[item._id] || '';
                          return isValidUrl(url);
                        });
                        return allItemsHaveValidUrls && summary.itemCount > 0 && !isNavigatingToCheckout;
                      })()
                        ? 'btn-primary'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isNavigatingToCheckout ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (() => {
                      const allItemsHaveValidUrls = cart.items.every(item => {
                        const url = urlInputs[item._id] || '';
                        return isValidUrl(url);
                      });
                      return allItemsHaveValidUrls;
                    })() ? (
                      <>
                        Proceed to Checkout
                        <ArrowRightIcon className="h-5 w-5 ml-2" />
                      </>
                    ) : (
                      'Enter URLs to Continue'
                    )}
                  </button>

                  {(() => {
                    const allItemsHaveValidUrls = cart.items.every(item => {
                      const url = urlInputs[item._id] || '';
                      return isValidUrl(url);
                    });
                    return !allItemsHaveValidUrls;
                  })() && (
                    <p className="text-sm text-red-600 mt-2 text-center">
                      Please enter valid URLs for all hosting plans
                    </p>
                  )}

                  {/* Additional Info */}
                  <div className="mt-6 space-y-3 text-sm text-gray-600">
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                      Secure payment with Square
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Instant setup after payment
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartPage; 